import { createFormContext, zodResolver } from '@mantine/form'
import { AxiosError } from 'axios'
import { useEffect } from 'react'

import {
  useMutationCreateUserAddress,
  useMutationUpdateUserAddress,
} from '@flecto/core/shared'
import { type CoreUserAddress } from '@flecto/types'
import { useHandleAxiosError } from '@flecto/utils'

import { useAddressDrawer } from '../../hooks/useAddressDrawer'
import { useAddressNotification } from '../../hooks/useAddressNotification'
import { useAddressSchema } from '../../hooks/useAddressSchema'
import { AddressBusinessHours } from './AddressBusinessHours/AddressBusinessHours'
import { AddressErrors } from './AddressErrors'
import { AddressFormFields } from './AddressFormFields'
import { AddressFormMap } from './AddressFormMap'
import { AddressWarnings } from './AddressWarnings'

export const [FormProvider, useFormContext, useForm] =
  createFormContext<Partial<CoreUserAddress>>()

type AddressFormProps = {
  id?: string
  initialAddress?: Partial<CoreUserAddress>
  onSuccess?: (address: CoreUserAddress) => void
  isModal?: boolean
  closeModal?: () => void
}

export function AddressForm({
  initialAddress,
  onSuccess,
  isModal = false,
  closeModal,
  ...props
}: AddressFormProps) {
  const { mutateAsync: createUserAddress } = useMutationCreateUserAddress()
  const { mutateAsync: updateUserAddress } = useMutationUpdateUserAddress()
  const { closeDrawer } = useAddressDrawer()
  const { showNotification } = useAddressNotification()
  const { schema, initialValues } = useAddressSchema()
  const { getErrorMessages } = useHandleAxiosError()

  const form = useForm({
    initialValues,
    validate: zodResolver(schema),
  })

  const saveAddress = async () => {
    try {
      const options = {
        onSuccess: (address: CoreUserAddress) => {
          onSuccess?.(address)
          if (isModal) {
            closeModal?.()
          } else {
            closeDrawer()
          }
          showNotification()
        },
      }

      if (!form.values.id) {
        await createUserAddress({ address: form.values }, options)
      } else {
        await updateUserAddress(
          { address: form.values, addressId: form.values.id },
          options
        )
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errors = getErrorMessages(error, {
          'user_address[address_postal_code]': 'address_postal_code',
        })
        errors.forEach((e) => {
          form.setFieldError(e.field, e.errorMessage)
        })
      }
    }
  }

  useEffect(() => {
    if (initialAddress) {
      form.setValues(initialAddress)
    } else {
      form.reset()
    }
  }, [initialAddress])

  return (
    <FormProvider form={form}>
      <form {...props} onSubmit={form.onSubmit(() => saveAddress())}>
        <AddressErrors />
        <AddressWarnings />
        <AddressFormFields />
        <AddressBusinessHours mt='lg' />
        <AddressFormMap mt='lg' />
      </form>
    </FormProvider>
  )
}
